import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { importProvidersFrom, ErrorHandler } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular/dist/esm2015';
import {
  UserServiceConfig,
  Helper,
  IfaaUiComponentsModule,
} from '@ifaa-components/ui-components';
import { MetaReducer, StoreModule } from '@ngrx/store';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgrxFormsModule } from 'ngrx-forms';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { MaterialModule } from './app/app.material.module';
import { SuperannuationProviderSearchEffects } from './app/components/superannuation-provider-search/effects';
import { MessagePipe } from './app/pipes/message';
import { AuthGuardService } from './app/route-guard/auth-guard.service';
import { PermissionGuardService } from './app/route-guard/permission-guard.service';
import { HttpCustomInterceptor } from './app/services/http-interceptor';
import { LeadService } from './app/services/lead.service';
import { CommonEffects } from './app/store/common/common.effects';
import { clearState } from './app/store/meta-reducer';
import { environment } from './environments/environment';
import { ToastrModule } from 'ngx-toastr';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { reducers } from './app/store/app.reducers';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { JourneyService } from './app/services/journey.service';
import { GenderService } from './app/services/gender.service';
import { NmjInitialDataEffects } from './app/views/member-join/steps/nmj-initial-data/effects';
import { NmjDetailsEffects } from './app/views/member-join/steps/nmj-details/effects';
import { NmjMobileVerificationEffects } from './app/views/member-join/steps/nmj-mobile-verification/effects';
import { NmjTfnEffects } from './app/views/member-join/steps/nmj-tfn/effects';
import { NmjRollinEffects } from './app/views/member-join/steps/nmj-rollin/effects';
import { RollinService } from './app/services/rollin.service';
import { NmjInvestmentsEffects } from './app/views/member-join/steps/nmj-investments/effects';
import { InvestmentsService } from './app/services/investments.service';
import { NmjReviewEffects } from './app/views/member-join/steps/nmj-review/effects';
import { NmjParentEffects } from './app/views/member-join/effects';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export const metaReducers: MetaReducer<any>[] = [clearState];
Bugsnag.start({
  apiKey: environment.bugsnagApiKey,
  releaseStage: environment.bugsnagEnv,
  enabledReleaseStages: ['Production', 'Staging', 'Uat', 'unknown'],
  onError: function (event) {
    event.addMetadata('tenant', {
      tenant: environment.code,
    });
  },
});

export function errorHandlerFactory() {
  return new BugsnagErrorHandler();
}

const imports = [
  NgrxFormsModule,
  BrowserModule,
  IfaaUiComponentsModule.forRoot({
    entryAppearance: 'outline',
    requiredIconInvalid: 'report',
    googleApiKey: environment.googleApiKey,
    apiUrl: environment.apiUrl,
  } as UserServiceConfig),
  CommonModule,
  FormsModule,
  ToastrModule.forRoot({
    timeOut: 6000,
    positionClass: 'toast-bottom-center',
    preventDuplicates: true,
  }),
  MaterialModule,
  BrowserAnimationsModule,
  AppRoutingModule,
  TranslateModule.forRoot({
    loader: {
      provide: TranslateLoader,
      useFactory: HttpLoaderFactory,
      deps: [HttpClient],
    },
  }),
  StoreModule.forRoot(reducers, { metaReducers }),
  StoreDevtoolsModule.instrument({
    maxAge: 20, // Retains last 25 states
    logOnly: environment.production, // Restrict extension to log-only mode
  }),
  ReactiveFormsModule,
  EffectsModule.forRoot([
    CommonEffects,
    SuperannuationProviderSearchEffects,
    NmjInitialDataEffects,
    NmjDetailsEffects,
    NmjMobileVerificationEffects,
    NmjTfnEffects,
    NmjRollinEffects,
    NmjInvestmentsEffects,
    NmjReviewEffects,
    NmjParentEffects
  ]),
];

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(imports),
    Helper,
    AuthGuardService,
    LeadService,
    JourneyService,
    GenderService,
    RollinService,
    InvestmentsService,
    MessagePipe,
    PermissionGuardService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpCustomInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useFactory: errorHandlerFactory },
    provideHttpClient(withInterceptorsFromDi()),
  ],
});
